import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { FroschHupfenExerciseConfig, FroschHupfenRunSettings } from "@calega/content";
import type { FroschHupfenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
interface Answer { itemId: string; value: number; correct: boolean }
interface State { runId: string; run: FroschHupfenRun; index: number; phase: Phase; endsAt: number | null; answers: Answer[]; result: { score: number; maxScore: number } | null }

const ranges: Array<{ id: FroschHupfenRunSettings["range"]; label: string }> = [{ id: "1-10", label: "1–10" }, { id: "1-20", label: "1–20" }];
const operations: Array<{ id: FroschHupfenRunSettings["operation"]; label: string }> = [{ id: "mixed", label: "± Gemischt" }, { id: "addition", label: "+ Addition" }, { id: "subtraction", label: "− Subtraktion" }];
const frogHopDurationMs = 520;

function FrogFigure() {
  return <svg className="frog-figure" viewBox="0 0 132 104" role="img" aria-label="Hüpfender Frosch">
    <g className="frog-shadow" aria-hidden="true"><ellipse cx="66" cy="96" rx="32" ry="5" /></g>
    <g className="frog-hind-leg frog-hind-leg-left">
      <path className="frog-thigh" d="M47 65 C32 64 22 72 20 84 C28 86 38 83 47 76 Z" />
      <path className="frog-shin" d="M27 80 C18 82 13 88 11 94 C20 96 30 92 37 85 Z" />
      <path className="frog-foot" d="M15 91 C9 90 5 92 2 96 M16 92 C11 95 10 98 9 101 M19 92 C18 97 20 100 22 102" />
    </g>
    <g className="frog-hind-leg frog-hind-leg-right">
      <path className="frog-thigh" d="M85 65 C100 64 110 72 112 84 C104 86 94 83 85 76 Z" />
      <path className="frog-shin" d="M105 80 C114 82 119 88 121 94 C112 96 102 92 95 85 Z" />
      <path className="frog-foot" d="M117 91 C123 90 127 92 130 96 M116 92 C121 95 122 98 123 101 M113 92 C114 97 112 100 110 102" />
    </g>
    <ellipse className="frog-body" cx="66" cy="59" rx="31" ry="29" />
    <ellipse className="frog-belly" cx="66" cy="65" rx="20" ry="20" />
    <g className="frog-front-leg frog-front-leg-left">
      <path d="M47 58 C39 63 36 72 34 82" />
      <path className="frog-front-foot" d="M34 81 C29 84 27 88 27 92 M34 82 C34 88 36 91 39 94 M35 81 C40 85 43 87 47 87" />
    </g>
    <g className="frog-front-leg frog-front-leg-right">
      <path d="M85 58 C93 63 96 72 98 82" />
      <path className="frog-front-foot" d="M98 81 C103 84 105 88 105 92 M98 82 C98 88 96 91 93 94 M97 81 C92 85 89 87 85 87" />
    </g>
    <path className="frog-head" d="M39 47 C39 25 49 14 66 14 C83 14 93 25 93 47 C87 55 79 59 66 59 C53 59 45 55 39 47 Z" />
    <g className="frog-eye frog-eye-left"><circle className="frog-eye-white" cx="49" cy="20" r="12" /><circle className="frog-pupil" cx="51" cy="21" r="5" /><circle className="frog-eye-glint" cx="53" cy="18" r="1.7" /></g>
    <g className="frog-eye frog-eye-right"><circle className="frog-eye-white" cx="83" cy="20" r="12" /><circle className="frog-pupil" cx="81" cy="21" r="5" /><circle className="frog-eye-glint" cx="83" cy="18" r="1.7" /></g>
    <circle className="frog-nostril" cx="59" cy="37" r="1.6" /><circle className="frog-nostril" cx="73" cy="37" r="1.6" />
    <path className="frog-mouth" d="M54 44 Q66 52 78 44" />
    <circle className="frog-spot" cx="47" cy="48" r="3" /><circle className="frog-spot" cx="88" cy="47" r="2.6" /><circle className="frog-spot" cx="82" cy="67" r="2.2" />
  </svg>;
}

function HoppingFrog({ itemId, start, result, maximum, active }: { itemId: string; start: number; result: number; maximum: number; active: boolean }) {
  const [position, setPosition] = useState(start);
  const [isHopping, setIsHopping] = useState(false);
  const [drawnSteps, setDrawnSteps] = useState(0);
  const [hopSequence, setHopSequence] = useState(0);
  const direction = result >= start ? 1 : -1;

  useEffect(() => {
    setPosition(start);
    setIsHopping(false);
    setDrawnSteps(0);
    setHopSequence(0);
  }, [itemId, start]);

  useEffect(() => {
    if (!active) {
      setPosition(start);
      setIsHopping(false);
      setDrawnSteps(0);
      setHopSequence(0);
      return;
    }
    let cancelled = false;
    let current = start;
    let timeout = 0;
    let frame = 0;
    setPosition(start);
    setDrawnSteps(0);
    setHopSequence(0);

    const nextHop = () => {
      if (cancelled || current === result) {
        setIsHopping(false);
        return;
      }
      setIsHopping(false);
      frame = window.requestAnimationFrame(() => {
        if (cancelled) return;
        current += direction;
        setPosition(current);
        setDrawnSteps((steps) => steps + 1);
        setHopSequence((sequence) => sequence + 1);
        setIsHopping(true);
        timeout = setExerciseTimeout(nextHop, frogHopDurationMs + 70);
      });
    };

    timeout = setExerciseTimeout(nextHop, 140);
    return () => {
      cancelled = true;
      clearExerciseTimeout(timeout);
      window.cancelAnimationFrame(frame);
    };
  }, [active, direction, itemId, result, start]);

  const style = {
    left: `${(position / maximum) * 100}%`,
    "--frog-hop-duration": `${frogHopDurationMs}ms`,
    "--frog-flight-tilt": direction > 0 ? "5deg" : "-5deg",
    "--frog-landing-tilt": direction > 0 ? "-2deg" : "2deg",
  } as CSSProperties;

  return <>
    {Array.from({ length: drawnSteps }, (_, index) => {
      const from = start + index * direction;
      const to = from + direction;
      const arcStyle = {
        left: `${(Math.min(from, to) / maximum) * 100}%`,
        width: `${100 / maximum}%`,
        "--frog-hop-duration": `${frogHopDurationMs}ms`,
      } as CSSProperties;
      const path = direction > 0 ? "M1 42 Q50 3 99 42" : "M99 42 Q50 3 1 42";
      return <svg key={`${itemId}-${index}`} className="frog-hop-arc" style={arcStyle} viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden="true">
        <path d={path} pathLength="1" vectorEffect="non-scaling-stroke" />
      </svg>;
    })}
    <span className={`frog-sprite ${isHopping ? "is-hopping" : ""}`} style={style}><FrogFigure key={`${itemId}-${hopSequence}`} /></span>
  </>;
}

export function FroschHupfen({ account, config, pageId }: { account: Account; config: FroschHupfenExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<FroschHupfenRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<FroschHupfenRunSettings["range"]>(config.defaultRange);
  const [operation, setOperation] = useState<FroschHupfenRunSettings["operation"]>(config.defaultOperation);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, tick] = useState(0);
  const score = useMemo(() => exercise?.answers.filter((answer) => answer.correct).length ?? 0, [exercise?.answers]);

  const publish = (state: State | null) => realtime.publishExercise({ exerciseType: "frosch-hupfen", pageId, state });
  useEffect(() => { const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null; if (synced?.exerciseType === "frosch-hupfen" && synced.pageId === pageId) setExercise(synced.state ?? null); }, [realtime.state.exercise, pageId]);
  useEffect(() => { if (!exercise?.endsAt || exercise.phase === "complete") return; const interval = window.setInterval(() => tick((value) => value + 1), 250); return () => window.clearInterval(interval); }, [exercise?.endsAt, exercise?.phase]);
  useEffect(() => { if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return; const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20); return () => clearExerciseTimeout(timeout); }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  const settings = (): FroschHupfenRunSettings => ({ mode, count, durationSeconds, range, operation });
  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: FroschHupfenRun }>({ contentPageId: pageId, settings: runSettings });
      const next: State = { runId: response.runId, run: response.exercise, index: 0, phase: "running", endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null, answers: [], result: null };
      setExercise(next); setMessage(""); publish(next);
    } finally { setBusy(false); }
  }
  async function abort() { if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId); }
  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try { await abort(); setMode(exercise.run.settings.mode); setCount(exercise.run.settings.count); setDurationSeconds(exercise.run.settings.durationSeconds); setRange(exercise.run.settings.range); setOperation(exercise.run.settings.operation); setExercise(null); setMessage(""); publish(null); } finally { setBusy(false); }
  }
  async function remix() { if (!exercise || busy || !canControl) return; const runSettings = exercise.run.settings; setBusy(true); try { await abort(); } finally { setBusy(false); } await start(runSettings); }
  async function answer(value: number) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index]; if (!item) return;
    setBusy(true);
    try { await submitExerciseAnswer(exercise.runId, { type: "frosch-hupfen", itemId: item.id, value }); const next: State = { ...exercise, phase: "feedback", answers: [...exercise.answers, { itemId: item.id, value, correct: value === item.result }] }; setExercise(next); publish(next); } finally { setBusy(false); }
  }
  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const }; setExercise(next); publish(next); setMessage("");
    });
  }
  async function closeCompletedRun(repeat: boolean) { if (!exercise || exercise.phase !== "complete") return; if (repeat) await remix(); else await backToSettings(); }
  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const nextState: State = { ...exercise, index: exercise.index + 1, phase: "running" }; setExercise(nextState); publish(nextState);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="frosch-exercise" />;
  if (!exercise) return <section className="frosch-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="mathe-settings frosch-settings">
      <fieldset><legend>Zahlenraum</legend><div className="mathe-option-grid">{ranges.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-frog-range`} checked={range === item.id} onChange={() => setRange(item.id)} />{item.label}</label>)}</div></fieldset>
      <fieldset><legend>Rechenart</legend><div className="mathe-option-grid">{operations.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-frog-operation`} checked={operation === item.id} onChange={() => setOperation(item.id)} />{item.label}</label>)}</div></fieldset>
      <fieldset><legend>Durchlauf</legend><div className="mathe-run-settings"><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-frog-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" name={`${pageId}-frog-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div>{mode === "count" ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}</div></fieldset>
    </div>
    <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Frosch-Hüpfen starten</button>{message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index]; const lastAnswer = exercise.answers.at(-1); const maximum = exercise.run.settings.range === "1-10" ? 10 : 20; const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  return <section className="frosch-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <><div className="frosch-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.length} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <div className="frosch-card"><div className="frosch-equation"><strong>{item.start}</strong><span>{item.operation}</span><strong>{item.steps}</strong><span>=</span><b>?</b></div><div className={`frosch-numberline range-${maximum} ${exercise.phase === "feedback" ? "resolved" : ""}`}><div className="frosch-track"><HoppingFrog itemId={item.id} start={item.start} result={item.result} maximum={maximum} active={exercise.phase === "feedback"} /></div><div className="frosch-ticks">{Array.from({ length: maximum + 1 }, (_, value) => <button key={value} className={`frosch-number ${exercise.phase === "feedback" && value === item.result ? "correct-target" : ""} ${exercise.phase === "feedback" && lastAnswer?.value === value && !lastAnswer.correct ? "wrong-target" : ""}`} disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => answer(value).catch((error) => setMessage(error.message))}><i />{value}</button>)}</div></div><p>Wo landet der Frosch? Tippe auf die Zahl.</p></div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig! " : "Noch nicht ganz. "}{item.start} {item.operation} {item.steps} = {item.result}</div>}
      <div className="frosch-actions">{exercise.phase === "feedback" ? canControl ? <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow) || exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Nächste Aufgabe"}</button> : <p className="muted">Die nächste Aufgabe wird von der Sitzungsleitung geöffnet.</p> : null}</div>
    </>}{message && <div className="alert info">{message}</div>}
  </section>;
}
